import { Children, isValidElement, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useFilterStore } from '../../stores/filterStore';

interface MasonryGridProps {
  children: ReactNode;
}

/**
 * Row-first masonry — fördelar barnen kronologiskt över N kolumner enligt
 * "shortest-column-next"-algoritmen. Det innebär att bild #1 hamnar i
 * vänster kolumn, #2 i nästa, #3 i nästa, osv. Nästa rad fortsätter där
 * en kolumn blir kortast (bilder med olika aspect ratio kompenseras), men
 * ordningen *uppfattas* horisontellt vänster→höger eftersom alla "första
 * raden"-bilder hamnar överst i sina respektive kolumner.
 *
 * Vi behöver känna till varje bilds aspect ratio för att kunna räkna ut
 * kolumn-höjder. Children förväntas ha en data-aspect-attribut (height/width)
 * eller så defaultar vi till porträtt 0.66 (3344×5040).
 */
export function MasonryGrid({ children }: MasonryGridProps) {
  const columnsSetting = useFilterStore((s) => s.columns);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Observera container-bredd för responsiv kolumn-cap
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Mobil-cap: max 2 kolumner under 800px (matchar v3-design)
  const effectiveColumns = useMemo(() => {
    if (containerWidth > 0 && containerWidth < 800) {
      return Math.min(columnsSetting, 2);
    }
    return columnsSetting;
  }, [columnsSetting, containerWidth]);

  // Distribuera barn till kolumner med shortest-column-first
  const columns = useMemo(() => {
    const cols: { items: ReactElement[]; height: number }[] = Array.from(
      { length: effectiveColumns },
      () => ({ items: [], height: 0 }),
    );
    const arr = Children.toArray(children).filter(isValidElement) as ReactElement[];

    arr.forEach((child, idx) => {
      // Första raden: tilldela strikt vänster→höger för perfekt kronologisk
      // ordning på första raden (bild 1 längst till vänster, etc.)
      if (idx < effectiveColumns) {
        const props = child.props as { 'data-aspect'?: number };
        const aspect = typeof props['data-aspect'] === 'number' ? props['data-aspect'] : 0.66;
        cols[idx].items.push(child);
        cols[idx].height += 1 / aspect; // höjd i "kolumnbredds-enheter"
        return;
      }
      // Resten: lägg till i kortaste kolumnen
      let shortest = 0;
      for (let i = 1; i < cols.length; i++) {
        if (cols[i].height < cols[shortest].height) shortest = i;
      }
      const props = child.props as { 'data-aspect'?: number };
      const aspect = typeof props['data-aspect'] === 'number' ? props['data-aspect'] : 0.66;
      cols[shortest].items.push(child);
      cols[shortest].height += 1 / aspect;
    });

    return cols;
  }, [children, effectiveColumns]);

  return (
    <div
      ref={containerRef}
      className="grid gap-2"
      style={{
        gridTemplateColumns: `repeat(${effectiveColumns}, minmax(0, 1fr))`,
      }}
    >
      {columns.map((col, i) => (
        <div key={i} className="flex flex-col gap-2 min-w-0">
          {col.items}
        </div>
      ))}
    </div>
  );
}
